import { Box, Flex, Icon, Show, Text } from '@chakra-ui/react';
import type { Msg, SessionOperate, User } from '@halo/chat-sdk';
import type { ReactNode } from 'react';
import { GroupIcon, RobotIcon, OfficialIcon } from '@halo/ui-chat-react';
import { ChatMetaExtra } from '../ChatMetaExtra';

interface ChatMetaContentProps {
  isGroup: boolean;
  unreadCount: number;
  mutedUnreadCount: number;
  lastMessage?: Msg;
  children: ReactNode;
  isSystemMsg?: boolean;
  hasMention?: boolean;
  isLuckyBox?: boolean;
  chatLines?: number;
  isMyMessage?: boolean;
  lastMsgUser?: User;
  draftMsg?: ReactNode;
  operateAccess?: Record<keyof typeof SessionOperate, boolean>;
  isCollapseChat?: boolean;
  groupName?: string;
  lastMessageChatIsGroup?: boolean;
  isRobot?: boolean;
}

export const ChatMetaContent = memo(function ChatMetaContent({
  isGroup,
  unreadCount,
  mutedUnreadCount,
  lastMessage,
  isSystemMsg,
  hasMention,
  isLuckyBox,
  children,
  chatLines,
  isMyMessage,
  lastMsgUser,
  draftMsg,
  operateAccess,
  isCollapseChat,
  groupName,
  lastMessageChatIsGroup,
  isRobot,
}: ChatMetaContentProps) {
  const isRow = useMemo(() => chatLines === 2, [chatLines]);
  return (
    <Box className="ChatMetaContent" display="flex">
      <Flex
        className="ChatMetaContentMsg"
        direction={isRow ? 'row' : 'column'}
        align={isRow ? 'center' : 'flex-start'}
        flex="1"
        overflow="hidden"
        minW={0}
        pointerEvents="none"
      >
        <Show
          when={
            (isGroup && !isSystemMsg && !draftMsg && lastMsgUser?.name) ||
            (isCollapseChat && !!children)
          }
        >
          <Flex
            alignItems="center"
            flex="0 0 auto"
            maxW={isRow ? '50%' : 'full'}
            overflow="hidden"
            fontWeight="medium"
            fontSize="xs"
          >
            {((isCollapseChat && isGroup) || lastMessageChatIsGroup) && (
              <Icon mr="1">
                <GroupIcon />
              </Icon>
            )}
            {isCollapseChat && isRobot && (
              <Icon mr="1" w="1.1em" h="1.1em">
                <RobotIcon />
              </Icon>
            )}
            <Text truncate flex={isRow ? '1' : 'auto'}>
              {isCollapseChat ? groupName : lastMsgUser?.name}
              {isCollapseChat && isRobot && <OfficialIcon width="1.1em" height="1.1em" />}
            </Text>
            <Text color="fg.muted" pr="1">
              :
            </Text>
          </Flex>
        </Show>
        {children}
      </Flex>
      <ChatMetaExtra
        isGroup={isGroup}
        lastMessage={lastMessage}
        unreadCount={unreadCount}
        mutedUnreadCount={mutedUnreadCount}
        hasMention={hasMention}
        isLuckyBox={isLuckyBox}
        isMyMessage={isMyMessage}
        isSystemMsg={isSystemMsg}
        operateAccess={operateAccess}
      />
    </Box>
  );
});
